import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Package, CheckSquare, Square, Filter, MousePointerClick, ExternalLink, Sparkles, Zap } from "lucide-react";

const CATEGORIES = ["Recovery", "Comfort", "Self Care", "Entertainment", "Nutrition"];

export default function CatalogBrowser() {
  const [merchants, setMerchants] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ network: "", merchant_id: "", q: "", auto_category: "", min_confidence: 0 });
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState({ impact: false, awin: false, ai: false });
  const [aiProgress, setAiProgress] = useState(null); // { processed, total, kept, dropped }
  const [clickStats, setClickStats] = useState(null);
  const [catSummary, setCatSummary] = useState(null);

  const loadCatSummary = async () => {
    try {
      const { data } = await api.get("/admin/staged/categories");
      setCatSummary(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load category summary:", err?.message);
    }
  };

  const loadMerchants = async () => {
    try {
      const { data } = await api.get("/admin/staged/merchants");
      setMerchants(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load merchants list:", err?.message);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.network) params.set("network", filter.network);
      if (filter.merchant_id) params.set("merchant_id", filter.merchant_id);
      if (filter.q) params.set("q", filter.q);
      if (filter.auto_category) params.set("auto_category", filter.auto_category);
      if (filter.min_confidence > 0) params.set("min_confidence", String(filter.min_confidence));
      params.set("limit", "100");
      const { data } = await api.get(`/admin/staged?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Couldn't load staged products.");
    } finally { setLoading(false); }
  };

  const loadClickStats = async () => {
    try {
      const { data } = await api.get("/admin/clicks");
      setClickStats(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load click stats:", err?.message);
    }
  };

  useEffect(() => { loadMerchants(); loadItems(); loadClickStats(); loadCatSummary(); }, []);
  useEffect(() => { loadItems(); setSelected(new Set()); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSync = async (which) => {
    setSyncing((s) => ({ ...s, [which]: true }));
    try {
      const { data } = await api.post(`/admin/sync/${which}`, {});

      // AWIN now returns immediately with a job_id (background task) — poll for completion
      if (data.job_id) {
        toast.message(`${which.toUpperCase()} sync started in background — large feed, ~60-90s…`);
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          if (attempts > 30) {  // ~5 minutes max
            toast.error("Sync is still running — check back in a minute.");
            setSyncing((s) => ({ ...s, [which]: false }));
            return;
          }
          try {
            const { data: status } = await api.get(`/admin/sync/status`, { params: { job_id: data.job_id } });
            if (status.status === "done") {
              toast.success(`${which.toUpperCase()} sync done: ${(status.items_staged || 0).toLocaleString()} products from ${status.merchants_seen || 0} merchants.`);
              await Promise.all([loadMerchants(), loadItems()]);
              setSyncing((s) => ({ ...s, [which]: false }));
            } else if (status.status === "failed") {
              toast.error(`${which.toUpperCase()} sync failed: ${status.error || "unknown error"}`);
              setSyncing((s) => ({ ...s, [which]: false }));
            } else {
              setTimeout(poll, 10000);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Sync status poll failed:", err?.message);
            setTimeout(poll, 10000);
          }
        };
        setTimeout(poll, 8000);
        return;
      }

      // Synchronous response (Impact path)
      const staged = data.items_staged || 0;
      const merchantsSeen = data.merchants_seen ?? data.catalogs_seen ?? 0;
      toast.success(`${which.toUpperCase()} sync done: ${staged.toLocaleString()} products from ${merchantsSeen} ${which === "awin" ? "merchants" : "catalogs"}.`);
      await Promise.all([loadMerchants(), loadItems()]);
      setSyncing((s) => ({ ...s, [which]: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${which} sync failed.`);
      setSyncing((s) => ({ ...s, [which]: false }));
    }
  };

  const runAiCategorize = async () => {
    setSyncing((s) => ({ ...s, ai: true }));
    try {
      const { data } = await api.post(`/admin/staged/auto-categorize?limit=20000`, {});
      if (!data.job_id) {
        toast.message(data.message || "Nothing to categorize.");
        setSyncing((s) => ({ ...s, ai: false }));
        return;
      }
      toast.message(`AI categorization started — processing ~${data.total_pending?.toLocaleString()} products in batches of 40. This may take a while.`);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        if (attempts > 600) {
          setSyncing((s) => ({ ...s, ai: false }));
          return;
        }
        try {
          const { data: status } = await api.get("/admin/sync/status", { params: { job_id: data.job_id } });
          setAiProgress({
            processed: status.processed || 0,
            total: status.total_pending || 0,
            kept: status.kept || 0,
            dropped: status.dropped || 0,
          });
          if (status.status === "done") {
            toast.success(`AI sorted ${status.processed?.toLocaleString()} products: ${status.kept?.toLocaleString()} relevant, ${status.dropped?.toLocaleString()} dropped.`);
            await loadCatSummary();
            await loadItems();
            setSyncing((s) => ({ ...s, ai: false }));
            setTimeout(() => setAiProgress(null), 10000);
          } else if (status.status === "failed") {
            toast.error(`AI categorization failed: ${status.error || "unknown error"}`);
            setSyncing((s) => ({ ...s, ai: false }));
          } else {
            if (attempts % 3 === 0) loadCatSummary();
            setTimeout(poll, 15000);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("AI status poll failed:", err?.message);
          setTimeout(poll, 15000);
        }
      };
      setTimeout(poll, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI categorization failed to start.");
      setSyncing((s) => ({ ...s, ai: false }));
    }
  };

  const importAllVisible = async (overrideCategory = null) => {
    if (total === 0) return;
    const targetCat = overrideCategory || filter.auto_category;
    if (!CATEGORIES.includes(targetCat) && !overrideCategory) {
      toast.error("Filter by an AI category first, or use the manual selection bar.");
      return;
    }
    if (!window.confirm(`Import all ${Math.min(total, 500)} filtered products into "${overrideCategory || targetCat}"?`)) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/staged/import-filtered", {
        network: filter.network || null,
        merchant_id: filter.merchant_id || null,
        auto_category: filter.auto_category || null,
        min_confidence: filter.min_confidence > 0 ? filter.min_confidence : null,
        only_relevant: filter.auto_category && filter.auto_category !== "irrelevant" ? true : null,
        override_category: overrideCategory,
        limit: 500,
      });
      toast.success(`Imported ${data.imported} products${data.skipped ? ` · ${data.skipped} skipped (no category)` : ""}.`);
      await loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed.");
    } finally { setImporting(false); }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const importSelected = async (category) => {
    if (!selected.size) return;
    if (!CATEGORIES.includes(category)) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/staged/import", {
        staged_ids: Array.from(selected),
        category,
      });
      toast.success(`Imported ${data.imported} product${data.imported === 1 ? "" : "s"} into ${category}.`);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setImporting(false); }
  };

  const merchantOptions = useMemo(() => {
    if (!filter.network) return merchants;
    return merchants.filter((m) => m.network === filter.network);
  }, [merchants, filter.network]);

  return (
    <div data-testid="admin-catalog-browser">
      {/* Click analytics row */}
      <div className="card-soft p-5 mb-6" data-testid="admin-click-stats">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-brand-soft flex items-center justify-center">
              <MousePointerClick className="h-5 w-5 text-brand" strokeWidth={1.6} />
            </div>
            <div>
              <p className="font-manrope text-xs text-ink-soft uppercase tracking-wider">Outbound clicks · last 30 days</p>
              <p className="font-outfit text-3xl font-medium text-ink leading-tight">
                {clickStats?.total_30d?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(clickStats?.by_network || []).map((n) => (
              <span key={n.network} className="tag-pill text-xs" data-testid={`click-network-${n.network}`}>
                {n.network}: {n.count}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Sync controls */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h3 className="font-outfit text-lg font-medium text-ink">Vendor catalogs</h3>
          <p className="font-manrope text-sm text-ink-soft mt-0.5">
            Pull products from Impact + AWIN, then bulk-add to your live registry.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => runSync("impact")}
            disabled={syncing.impact}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8] hover:bg-brand-soft/40 disabled:opacity-60"
            data-testid="admin-sync-impact-btn"
          >
            <RefreshCw className={`h-4 w-4 ${syncing.impact ? "animate-spin" : ""}`} />
            {syncing.impact ? "Syncing Impact…" : "Sync Impact"}
          </button>
          <button
            onClick={() => runSync("awin")}
            disabled={syncing.awin}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8] hover:bg-brand-soft/40 disabled:opacity-60"
            data-testid="admin-sync-awin-btn"
          >
            <RefreshCw className={`h-4 w-4 ${syncing.awin ? "animate-spin" : ""}`} />
            {syncing.awin ? "Syncing AWIN…" : "Sync AWIN"}
          </button>
          <button
            onClick={runAiCategorize}
            disabled={syncing.ai}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-brand text-white hover:bg-brand/90 disabled:opacity-60"
            data-testid="admin-ai-categorize-btn"
          >
            <Sparkles className={`h-4 w-4 ${syncing.ai ? "animate-pulse" : ""}`} />
            {syncing.ai ? "AI sorting…" : "AI auto-categorize"}
          </button>
        </div>
      </div>

      {/* AI progress + category summary */}
      {(aiProgress || catSummary) && (
        <div className="card-soft p-5 mb-6" data-testid="admin-ai-summary">
          {aiProgress && aiProgress.total > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-manrope text-sm text-ink-soft inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-brand animate-pulse" />
                  AI sorting in progress · {aiProgress.processed.toLocaleString()} / {aiProgress.total.toLocaleString()}
                </p>
                <p className="font-manrope text-xs text-ink-soft">
                  {aiProgress.kept.toLocaleString()} kept · {aiProgress.dropped.toLocaleString()} dropped
                </p>
              </div>
              <div className="h-1.5 bg-brand-soft rounded-full overflow-hidden">
                <div className="h-full bg-brand transition-all duration-500" style={{ width: `${Math.min(100, (aiProgress.processed / aiProgress.total) * 100)}%` }} />
              </div>
            </div>
          )}
          {catSummary && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <p className="font-manrope text-xs text-ink-soft uppercase tracking-wider">
                  AI-classified · {catSummary.processed.toLocaleString()} of {catSummary.total.toLocaleString()} processed
                </p>
                {catSummary.uncategorized > 0 && (
                  <p className="font-manrope text-xs text-ink-soft">
                    {catSummary.uncategorized.toLocaleString()} unprocessed
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((cat) => {
                  const count = catSummary.by_category[cat] || 0;
                  const active = filter.auto_category === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setFilter((f) => ({ ...f, auto_category: active ? "" : cat }))}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#EBE2E8] hover:bg-brand-soft/40"}`}
                      data-testid={`admin-cat-chip-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {cat} <span className="opacity-70">· {count.toLocaleString()}</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => setFilter((f) => ({ ...f, auto_category: filter.auto_category === "irrelevant" ? "" : "irrelevant" }))}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium border ${filter.auto_category === "irrelevant" ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-[#EBE2E8] hover:bg-gray-100"}`}
                  data-testid="admin-cat-chip-irrelevant"
                >
                  Irrelevant <span className="opacity-70">· {catSummary.irrelevant.toLocaleString()}</span>
                </button>
              </div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <label className="font-manrope text-xs text-ink-soft inline-flex items-center gap-2">
                  Min confidence:
                  <select
                    value={filter.min_confidence}
                    onChange={(e) => setFilter((f) => ({ ...f, min_confidence: parseFloat(e.target.value) }))}
                    className="text-xs bg-white border border-[#EBE2E8] rounded-md px-2 py-1"
                    data-testid="admin-confidence-filter"
                  >
                    <option value="0">Any</option>
                    <option value="0.5">≥ 0.5 (Medium)</option>
                    <option value="0.7">≥ 0.7 (High)</option>
                    <option value="0.85">≥ 0.85 (Very high)</option>
                  </select>
                </label>
                {filter.auto_category && filter.auto_category !== "irrelevant" && total > 0 && (
                  <button
                    onClick={() => importAllVisible()}
                    disabled={importing}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium bg-brand text-white hover:bg-brand/90 disabled:opacity-60"
                    data-testid="admin-import-all-filtered-btn"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Import all {Math.min(total, 500).toLocaleString()} → {filter.auto_category}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="relative">
          <Search className="h-4 w-4 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadItems()}
            placeholder="Search product name…"
            className="input-soft pl-10"
            data-testid="admin-catalog-search-input"
          />
        </div>
        <select
          value={filter.network}
          onChange={(e) => setFilter((f) => ({ ...f, network: e.target.value, merchant_id: "" }))}
          className="input-soft"
          data-testid="admin-catalog-network-filter"
        >
          <option value="">All networks</option>
          <option value="impact">Impact</option>
          <option value="awin">AWIN</option>
        </select>
        <select
          value={filter.merchant_id}
          onChange={(e) => setFilter((f) => ({ ...f, merchant_id: e.target.value }))}
          className="input-soft"
          data-testid="admin-catalog-merchant-filter"
        >
          <option value="">All merchants</option>
          {merchantOptions.map((m) => (
            <option key={`${m.network}-${m.merchant_id}`} value={m.merchant_id}>
              {m.merchant_name} ({m.count})
            </option>
          ))}
        </select>
        <button
          onClick={loadItems}
          className="rounded-full px-4 py-2 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8] inline-flex items-center justify-center gap-2"
          data-testid="admin-catalog-refresh-btn"
        >
          <Filter className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Bulk import bar */}
      {selected.size > 0 && (
        <div className="card-soft p-4 mb-4 flex items-center justify-between flex-wrap gap-3 bg-brand-soft/40 border-brand/30" data-testid="admin-catalog-import-bar">
          <p className="font-manrope text-sm text-ink">
            <strong>{selected.size}</strong> selected — import into:
          </p>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => importSelected(cat)}
                disabled={importing}
                className="rounded-full px-3.5 py-1.5 text-xs font-medium bg-brand text-white hover:bg-brand/90 disabled:opacity-60"
                data-testid={`admin-catalog-import-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              >
                + {cat}
              </button>
            ))}
            <button
              onClick={() => setSelected(new Set())}
              className="rounded-full px-3.5 py-1.5 text-xs font-medium bg-white text-ink-soft border border-[#EBE2E8]"
              data-testid="admin-catalog-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <p className="font-manrope text-sm text-ink-soft mb-3">
        {loading ? "Loading…" : `Showing ${items.length} of ${total.toLocaleString()} staged products`}
      </p>

      {/* Items grid */}
      {items.length === 0 && !loading ? (
        <div className="card-soft p-10 text-center" data-testid="admin-catalog-empty">
          <Package className="h-8 w-8 text-brand mx-auto mb-3 opacity-40" />
          <p className="font-outfit text-lg text-ink">No staged products yet.</p>
          <p className="font-manrope text-sm text-ink-soft mt-2">
            Click <strong>Sync Impact</strong> or <strong>Sync AWIN</strong> above to pull merchant catalogs.
          </p>
        </div>
      ) : (
        <div>
          <button
            onClick={toggleAll}
            className="mb-3 inline-flex items-center gap-2 font-manrope text-sm text-ink-soft hover:text-brand"
            data-testid="admin-catalog-toggle-all"
          >
            {selected.size === items.length ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            {selected.size === items.length ? "Deselect all" : "Select all on page"}
          </button>
          <div className="grid gap-2.5">
            {items.map((it) => {
              const isSel = selected.has(it.id);
              return (
                <div
                  key={it.id}
                  onClick={() => toggle(it.id)}
                  className={`card-soft p-3 flex items-center gap-3 cursor-pointer transition-colors ${isSel ? "ring-2 ring-brand bg-brand-soft/30" : "hover:bg-brand-soft/20"}`}
                  data-testid={`admin-catalog-item-${it.id}`}
                >
                  <div className="shrink-0">
                    {isSel ? <CheckSquare className="h-5 w-5 text-brand" /> : <Square className="h-5 w-5 text-ink-soft" />}
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-brand-soft/60 overflow-hidden shrink-0 flex items-center justify-center">
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : <Package className="h-4 w-4 text-brand opacity-60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.auto_category && (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 bg-brand-soft text-brand"
                          title={it.auto_reason}
                          data-testid={`admin-product-ai-cat-${it.id}`}
                        >
                          <Sparkles className="h-3 w-3" />
                          {it.auto_category}
                          {typeof it.auto_confidence === "number" && (
                            <span className="opacity-70">{Math.round(it.auto_confidence * 100)}%</span>
                          )}
                        </span>
                      )}
                      {it.auto_relevant === false && (
                        <span className="text-xs font-medium rounded-full px-2 py-0.5 bg-gray-100 text-ink-soft" title={it.auto_reason}>
                          irrelevant
                        </span>
                      )}
                      <span className="tag-pill text-xs">{it.network}</span>
                      <span className="font-manrope text-xs text-ink-soft">{it.merchant_name}</span>
                      {it.price > 0 && <span className="font-manrope text-xs text-ink-soft">· ${it.price.toFixed(2)}</span>}
                      {it.vendor_category && <span className="font-manrope text-xs text-ink-soft truncate">· {it.vendor_category}</span>}
                    </div>
                    <p className="mt-0.5 font-outfit text-sm text-ink truncate">{it.product_name}</p>
                    {it.auto_reason && (
                      <p className="mt-0.5 font-manrope text-xs text-ink-soft truncate italic" title={it.auto_reason}>
                        {it.auto_reason}
                      </p>
                    )}
                  </div>
                  <a
                    href={it.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 rounded-full hover:bg-brand-soft shrink-0"
                    title="Preview link"
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-ink-soft" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
